import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';
import { Link } from 'react-router-dom';

import { ButtonGroup, IconButton } from '@/components/button';
import { useLayoutContext } from '@/components/layout/layout-context';
import { Tooltip } from '@/components/tooltip';
import { buildExplorationUrl, TabParameter } from '@/explore/utils/url';
import { useBuildAccountUrl } from '@/lib/accounts/context';
import { useTrackEvent } from '@/lib/analytics';
import { getCleanedExplorationName, getExplorationLayout } from '@/core/exploration';
import { getExplorationTabs } from '@/core/exploration';

import { Icon } from '../../components/icon';
import { getExplorationIconName, isModelDetailExploration } from '../utils';
import { useExplorationUIContext } from './exploration-ui-context';

import { Exploration, ExplorationLayout } from '../types';
import { ExplorationTabs } from './exploration-tabs';

import styles from './exploration.module.scss';

interface ExplorationHeaderProps {
  exploration: Exploration;
  onMoveTab: (fromIndex: number, toIndex: number, after: boolean) => void;
  onMoveCellToTab: (cellId: string, tabId: string) => void;
  onLayoutChange: (layout: ExplorationLayout) => void;
}

export const ExplorationHeader = (props: ExplorationHeaderProps) => {
  const { exploration, onMoveTab, onMoveCellToTab, onLayoutChange } = props;
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const buildAccountUrl = useBuildAccountUrl();
  const [, copyToClipboard] = useCopyToClipboard();
  const trackEvent = useTrackEvent();
  const { activeTabId, getExplorationLink } = useExplorationUIContext();
  const { isRightSidebarOpen, toggleRightSidebar } = useLayoutContext();

  const { isDirty } = useExplorationUIContext();

  const handleShare = async () => {
    const explorationUrl = getExplorationLink(
      activeTabId !== null ? { [TabParameter]: activeTabId } : undefined,
    );
    const url = new URL(explorationUrl, location.origin);
    await copyToClipboard(url.toString());

    setShareLinkCopied(true);
    trackEvent('Share Link Copied', { explorationId: exploration.explorationId });

    setTimeout(() => setShareLinkCopied(false), 3000);
  };

  const baseExplorationUrl = useMemo(() => {
    if (exploration.options?.explorationSourceId === undefined) {
      return '#';
    }

    return buildAccountUrl(
      buildExplorationUrl({
        explorationId: exploration.options.explorationSourceId,
      }),
    );
  }, [buildAccountUrl, exploration.options?.explorationSourceId]);

  const explorationIconName = getExplorationIconName(exploration);
  const isDetailExploration = isModelDetailExploration(exploration);
  const tabs = getExplorationTabs(exploration);

  return (
    <>
      <header className={styles.header}>
        <div className={classNames(styles.explorationTitle, { [styles.dirtyTitle]: isDirty })}>
          {isDirty && <div className={styles.titleStatus}>Exploring from</div>}
          <Link className={styles.resetButton} title="Reset exploration" to={baseExplorationUrl}>
            <h1>
              <Icon name={explorationIconName} size={24} />
              {getCleanedExplorationName(exploration.name)}
            </h1>
          </Link>
          {isDetailExploration && <div className={styles.templateTag}>Template</div>}
        </div>

        <div className={styles.headerTools}>
          <Tooltip content={shareLinkCopied ? 'Copied!' : 'Copy share link'}>
            <IconButton
              icon="Link"
              onClick={handleShare}
              onMouseLeave={() =>
                // Account for tooltip hide delay
                setTimeout(() => setShareLinkCopied(false), 500)
              }
            />
          </Tooltip>
          <Tooltip content={`${isRightSidebarOpen ? 'Collapse' : 'Expand'} right sidebar`}>
            <IconButton
              icon={isRightSidebarOpen ? 'SidebarRightActive' : 'SidebarRight'}
              className={styles.sidebarToggle}
              onClick={(e) => {
                const newSidebarState = !isRightSidebarOpen;
                toggleRightSidebar(newSidebarState);
                trackEvent(newSidebarState ? 'Right Sidebar Opened' : 'Right Sidebar Closed');
                (e.currentTarget as HTMLButtonElement).blur();
              }}
            />
          </Tooltip>
          <hr className={styles.headerToolsSpacer} />
          <ButtonGroup
            value={getExplorationLayout(exploration)}
            size="small"
            options={[
              { value: 'notebook', label: 'Notebook' },
              { value: 'canvas', label: 'Canvas' },
            ]}
            onChange={(value) => onLayoutChange(value === 'canvas' ? 'canvas' : 'notebook')}
          />
        </div>
      </header>
      {tabs.length > 1 && (
        <div className={styles.headerRow}>
          <ExplorationTabs
            exploration={exploration}
            activeTabId={activeTabId}
            onDropCell={onMoveCellToTab}
            onMoveTab={onMoveTab}
          />
        </div>
      )}
    </>
  );
};
